import { relations } from "drizzle-orm/_relations";
import {
  boolean,
  doublePrecision,
  index,
  integer,
  numeric,
  pgTable,
  serial,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { levels } from "./levels";
import { invoices } from "./payments";
import { poolTeams } from "./pool-teams";
import { type TeamStatus, teamStatusEnum, timestamps } from "./shared";
import { teams } from "./teams";
import { tournamentDivisions } from "./tournament-divisions";

/**
 * Returns the `{confirmedAt, waitlistedAt}` pair to set when inserting or
 * updating a tournament_division_teams row to the given status. Timestamps
 * are truncated to the second so stored values align with Postgres display
 * precision and are easier to reason about in tests and logs.
 */
export function statusTimestamps(status: TeamStatus): {
  confirmedAt: Date | null;
  waitlistedAt: Date | null;
} {
  const now = new Date();
  now.setMilliseconds(0);
  return {
    confirmedAt: status === "confirmed" || status === "registered" ? now : null,
    waitlistedAt: status === "waitlisted" ? now : null,
  };
}

const { createInsertSchema, createSelectSchema, createUpdateSchema } =
  createSchemaFactory({ zodInstance: z });

export const tournamentDivisionTeams = pgTable(
  "tournament_division_teams",
  {
    id: serial().primaryKey(),
    tournamentDivisionId: integer()
      .notNull()
      .references(() => tournamentDivisions.id, { onDelete: "cascade" }),
    teamId: integer()
      .notNull()
      .references(() => teams.id),
    invoiceId: integer().references(() => invoices.id, {
      onDelete: "set null",
    }),
    pricePaid: numeric({ mode: "number" }),
    seed: integer(),
    finish: integer(),
    playoffsSeed: integer(),
    wildcard: boolean(),
    pointsEarned: doublePrecision(),
    levelEarnedId: integer().references(() => levels.id),
    status: teamStatusEnum().notNull().default("registered"),
    transferChargeInvoiceId: integer().references(() => invoices.id, {
      onDelete: "set null",
    }),
    transferChargeAmount: numeric({ mode: "number" }),
    withdrawnAt: timestamp(),
    withdrawnBy: text(),
    waitlistedAt: timestamp(),
    confirmedAt: timestamp(),
    // How this team reached "confirmed" from the waitlist: a user id for a
    // manual promotion, or "auto:<trigger>" (drop/capacity/transfer) for an
    // automatic one. Null if never promoted (registered directly).
    promotedBy: text(),
    externalRef: uuid().unique(),
    order: integer(),
    originalPlayerIds: integer().array(),
    managedByUserIds: text().array(),
    forfeited: boolean().default(false),
    ...timestamps,
  },
  (table) => [
    index("tdt_team_id_idx").on(table.teamId),
    index("tdt_status_idx").on(table.status),
    index("tdt_status_team_id_idx").on(table.status, table.teamId),
    index("tdt_tournament_division_id_idx").on(table.tournamentDivisionId),
    index("tdt_tournament_division_id_status_idx").on(
      table.tournamentDivisionId,
      table.status,
    ),
    index("tdt_invoice_id_idx").on(table.invoiceId),
    index("tdt_level_earned_id_idx").on(table.levelEarnedId),
    index("tdt_transfer_charge_invoice_id_idx").on(
      table.transferChargeInvoiceId,
    ),
  ],
);

export const selectTournamentDivisionTeamSchema = createSelectSchema(
  tournamentDivisionTeams,
);
export const createTournamentDivisionTeamSchema = createInsertSchema(
  tournamentDivisionTeams,
).omit({
  id: true,
});
export const updateTournamentDivisionTeamSchema = createUpdateSchema(
  tournamentDivisionTeams,
);

export type TournamentDivisionTeam = z.infer<
  typeof selectTournamentDivisionTeamSchema
>;
export type CreateTournamentDivisionTeam = z.infer<
  typeof createTournamentDivisionTeamSchema
>;
export type UpdateTournamentDivisionTeam = z.infer<
  typeof updateTournamentDivisionTeamSchema
>;

export const tournamentDivisionTeamsRelations = relations(
  tournamentDivisionTeams,
  ({ one }) => ({
    team: one(teams, {
      fields: [tournamentDivisionTeams.teamId],
      references: [teams.id],
    }),
    tournamentDivision: one(tournamentDivisions, {
      fields: [tournamentDivisionTeams.tournamentDivisionId],
      references: [tournamentDivisions.id],
    }),
    poolTeam: one(poolTeams, {
      fields: [tournamentDivisionTeams.id],
      references: [poolTeams.teamId],
    }),
    levelEarned: one(levels, {
      fields: [tournamentDivisionTeams.levelEarnedId],
      references: [levels.id],
    }),
    transferChargeInvoice: one(invoices, {
      fields: [tournamentDivisionTeams.transferChargeInvoiceId],
      references: [invoices.id],
    }),
  }),
);
